import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult } from '../voyage/schemas.js';
import { COST_METHODS } from './analysis.js';
import {
  type DecInput,
  decAdd,
  decCmp,
  decDiv,
  decMul,
  decNeg,
  decSub,
  decSum,
  formatDec,
} from './decimal.js';
import { optAmount, resultOf, typed } from './fields.js';
import type { LedgerEntry } from './ledger.js';
import { SOUNDING_METHODS, type SoundingConversion, type SoundingMethod } from './measurement.js';

/**
 * ROB mutabakatı (ADR 0117 §4, Ek Y4; ortak belgesi §10, ekran 06): ölçülen − hesaplanan (MT ve
 * %), tank, yakıt ve genel toplam bazında. Hesaplanan ROB defterin ölçüm anındaki bakiyesidir.
 * Eşik şirket ve ölçüm yöntemi parametresidir; koda gömülü genel tolerans yoktur: eşik yoksa
 * sonuç "eşik tanımsız"dır. Ay kapanışını engelleyenler (`closeBlockers`) burada türetilir. Saf
 * modül.
 */

/** Yöntemin eşiği: mutlak MT ve yüzde; ikisi de boşsa tanımsız. */
export interface ReconcileThreshold {
  mt: string | null;
  pct: string | null;
}
export type ReconcileThresholds = Partial<Record<SoundingMethod, ReconcileThreshold>>;
export type ThresholdStatus = 'within' | 'exceeds' | 'undefined';

const abs = (value: DecInput) => (decCmp(value, '0') < 0 ? decNeg(value) : value);

/** Farkın eşiğe göre durumu: MT ya da yüzde sınırlarından biri aşılırsa `exceeds`. */
export function thresholdStatus(
  diffMt: string,
  diffPct: string | null,
  threshold: ReconcileThreshold | null | undefined,
): ThresholdStatus {
  if (!threshold || (threshold.mt === null && threshold.pct === null)) return 'undefined';
  if (threshold.mt !== null && decCmp(abs(diffMt), threshold.mt) > 0) return 'exceeds';
  if (threshold.pct !== null && diffPct !== null && decCmp(abs(diffPct), threshold.pct) > 0) {
    return 'exceeds';
  }
  return 'within';
}

/** Yüzde fark = fark / hesaplanan × 100 (iki ondalık); hesaplanan sıfırsa `null`. */
function pctOf(diff: DecInput, calculated: DecInput): string | null {
  if (decCmp(calculated, '0') === 0) return null;
  return formatDec(decDiv(decMul(diff, '100', 9), calculated, 2));
}

export interface RobComparisonRow {
  calculatedMt: string;
  measuredMt: string;
  diffMt: string;
  diffPct: string | null;
  status: ThresholdStatus;
}

export interface RobComparison {
  tanks: (RobComparisonRow & { tankId: string; fuelTypeId: string | null; method: string })[];
  fuels: (RobComparisonRow & { fuelTypeId: string })[];
  total: RobComparisonRow;
  /** Hesaplanan bakiyesi olan ama ölçülmemiş tanklar (veri kalitesi: "eksik tank"). */
  unmeasuredTankIds: string[];
}

/** Birden çok satırın durumu: biri aşıyorsa aşar; biri tanımsızsa tanımsız; yoksa içinde. */
function combine(statuses: readonly ThresholdStatus[]): ThresholdStatus {
  if (statuses.includes('exceeds')) return 'exceeds';
  if (statuses.length === 0 || statuses.includes('undefined')) return 'undefined';
  return 'within';
}

/**
 * Karşılaştırma: yalnız ölçülen tanklar (ölçülmeyen tankın hesaplanan bakiyesi toplamı
 * bozmasın). Yakıt sırası satır sırasıdır.
 */
export function compareRob(input: {
  lines: readonly { tankId: string; method: string; measuredMt: string }[];
  calculatedByTank: Readonly<Record<string, string>>;
  fuelOfTank: Readonly<Record<string, string | null>>;
  thresholds: ReconcileThresholds;
}): RobComparison {
  const row = (calculated: DecInput, measured: DecInput, status: ThresholdStatus) => {
    const diff = formatDec(decSub(measured, calculated));
    return {
      calculatedMt: formatDec(calculated),
      measuredMt: formatDec(measured),
      diffMt: diff,
      diffPct: pctOf(diff, calculated),
      status,
    };
  };
  const tanks = input.lines.map((line) => {
    const calculated = input.calculatedByTank[line.tankId] ?? '0';
    const diff = formatDec(decSub(line.measuredMt, calculated));
    const threshold = input.thresholds[line.method as SoundingMethod];
    return {
      tankId: line.tankId,
      fuelTypeId: input.fuelOfTank[line.tankId] ?? null,
      method: line.method,
      ...row(
        calculated,
        line.measuredMt,
        thresholdStatus(diff, pctOf(diff, calculated), threshold),
      ),
    };
  });
  const fuelIds = [...new Set(tanks.map((t) => t.fuelTypeId).filter((f): f is string => !!f))];
  const fuels = fuelIds.map((fuelTypeId) => {
    const own = tanks.filter((t) => t.fuelTypeId === fuelTypeId);
    return {
      fuelTypeId,
      ...row(
        decSum(own.map((t) => t.calculatedMt)),
        decSum(own.map((t) => t.measuredMt)),
        combine(own.map((t) => t.status)),
      ),
    };
  });
  const measured = new Set(input.lines.map((l) => l.tankId));
  return {
    tanks,
    fuels,
    total: row(
      decSum(tanks.map((t) => t.calculatedMt)),
      decSum(tanks.map((t) => t.measuredMt)),
      combine(tanks.map((t) => t.status)),
    ),
    unmeasuredTankIds: Object.entries(input.calculatedByTank)
      .filter(([id, rob]) => !measured.has(id) && decCmp(rob, '0') !== 0)
      .map(([id]) => id),
  };
}

/* ---------- Ay kapanışı (ADR 0117 §10, Ek Y4) ---------- */

/** Ay (`YYYY-MM`) → UTC sınırları `[start, end)`. */
export function monthBounds(month: string): { start: string; end: string } {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) throw new DomainError('INVALID_MONTH', month);
  const [year, m] = month.split('-').map(Number) as [number, number];
  return {
    start: new Date(Date.UTC(year, m - 1, 1)).toISOString(),
    end: new Date(Date.UTC(year, m, 1)).toISOString(),
  };
}

export type CloseBlockerCode =
  | 'NEGATIVE_ROB'
  | 'CAPACITY_EXCEEDED'
  | 'MISSING_CONVERSION_BASIS'
  | 'UNAPPROVED_ADJUSTMENT'
  | 'UNMATCHED_CONSUMPTION';

export interface CloseBlocker {
  code: CloseBlockerCode;
  tankId?: string;
  at?: string;
  soundingId?: string;
  recordId?: string;
  recordNo?: string;
  reportId?: string;
}

/**
 * Ay kapanışını engelleyenler (ortak belgesi §10 "Blokajlar"): ay içinde herhangi bir anda negatif
 * tank bakiyesi ya da kapasite aşımı (hacim = bakiye / son bilinen yoğunluk), dayanaksız dönüşümlü
 * ölçüm satırı, onaylanmamış (taslak, iade ya da bekleyen) düzeltme talebi ve seferi olmayan
 * tüketim. Tank başına ilk an bildirilir. Sıra: kod sırası.
 */
export function closeBlockers(input: {
  start: string;
  end: string;
  entries: readonly LedgerEntry[];
  tanks: readonly { id: string; capacityM3: string }[];
  soundingLines: readonly {
    soundingId: string;
    tankId: string;
    measuredAt: string;
    conversion: SoundingConversion | string;
    basis: string | null;
  }[];
  openAdjustments: readonly { id: string; recordNo: string }[];
  unmatched: readonly { reportId: string; at: string }[];
}): CloseBlocker[] {
  const start = Date.parse(input.start);
  const end = Date.parse(input.end);
  const sorted = [...input.entries].sort(
    (a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt) || a.id.localeCompare(b.id),
  );
  const rob = new Map<string, string>();
  const density = new Map<string, string>();
  const negative = new Map<string, string>();
  const over = new Map<string, string>();
  const capacity = new Map(input.tanks.map((t) => [t.id, t.capacityM3]));
  const check = (tankId: string, at: string) => {
    const value = rob.get(tankId) ?? '0';
    if (decCmp(value, '0') < 0 && !negative.has(tankId)) negative.set(tankId, at);
    const d = density.get(tankId);
    const cap = capacity.get(tankId);
    if (d && cap && decCmp(decDiv(value, d, 6), cap) > 0 && !over.has(tankId)) {
      over.set(tankId, at);
    }
  };
  for (const entry of sorted) {
    const at = Date.parse(entry.occurredAt);
    if (at >= end) break;
    rob.set(entry.tankId, formatDec(decAdd(rob.get(entry.tankId) ?? '0', entry.massMt)));
    if (entry.density15 !== null && !entry.massMt.startsWith('-')) {
      density.set(entry.tankId, entry.density15);
    }
    if (at >= start) check(entry.tankId, entry.occurredAt);
  }
  // Ayın başında süren durum (önceki aydan devreden)
  for (const tankId of rob.keys()) {
    const firstInMonth = sorted.find(
      (e) => e.tankId === tankId && Date.parse(e.occurredAt) >= start,
    );
    if (!firstInMonth) check(tankId, input.start);
  }
  const blockers: CloseBlocker[] = [];
  for (const [tankId, at] of negative) blockers.push({ code: 'NEGATIVE_ROB', tankId, at });
  for (const [tankId, at] of over) blockers.push({ code: 'CAPACITY_EXCEEDED', tankId, at });
  for (const line of input.soundingLines) {
    const at = Date.parse(line.measuredAt);
    if (at < start || at >= end) continue;
    const manual = line.conversion === 'manual' || line.conversion === 'manual_volume';
    if (manual && (line.basis === null || line.basis.trim() === '')) {
      blockers.push({
        code: 'MISSING_CONVERSION_BASIS',
        tankId: line.tankId,
        soundingId: line.soundingId,
        at: line.measuredAt,
      });
    }
  }
  for (const adjustment of input.openAdjustments) {
    blockers.push({
      code: 'UNAPPROVED_ADJUSTMENT',
      recordId: adjustment.id,
      recordNo: adjustment.recordNo,
    });
  }
  for (const item of input.unmatched) {
    const at = Date.parse(item.at);
    if (at < start || at >= end) continue;
    blockers.push({ code: 'UNMATCHED_CONSUMPTION', reportId: item.reportId, at: item.at });
  }
  return blockers;
}

/* ---------- Şirket ayarı (geçici; Y5'te parametre setine taşınır) ---------- */

const thresholdSchema = z.strictObject({
  mt: optAmount(),
  pct: optAmount({ max: '100' }),
});

/**
 * Mutabakat eşikleri (ölçüm yöntemi başına MT ve %) ve maliyet yöntemi (ADR 0117 §1, Ek Y4): şirket
 * verisi, ofiste yazılır, gemiye iner. Tanımsız yöntem eşiksizdir (koda gömülü tolerans yok).
 */
export const fuelSettingsSchema = z.strictObject({
  thresholds: z
    .partialRecord(z.enum(SOUNDING_METHODS), thresholdSchema)
    .nullish()
    .transform((value) => value ?? {}),
  costMethod: z
    .enum(COST_METHODS, typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? null),
});
export type FuelSettingsInput = z.output<typeof fuelSettingsSchema>;

export function parseFuelSettings(input: unknown): ParseResult<FuelSettingsInput> {
  return resultOf(fuelSettingsSchema.safeParse(input));
}
